import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { HeronMark } from './components/icons';
import { Button, EmptyState } from './components/ui';
import { useAuth, useCapabilities } from './lib/auth';
import { CompaniesPage } from './pages/Companies';
import { CompanyDetailPage } from './pages/CompanyDetail';
import { DashboardPage } from './pages/Dashboard';
import { DemoDataPage } from './pages/DemoData';
import { LicensesPage } from './pages/Licenses';
import { LoginPage } from './pages/Login';
import { ProductsPage } from './pages/Products';
import { AuditLogPage, EmailOutboxPage } from './pages/Records';
import { SetPasswordPage } from './pages/SetPassword';
import { SourcesPage } from './pages/Sources';
import { StaffPage } from './pages/Staff';

export function App() {
  return (
    <Routes>
      {/* The one public route: where an invite email lands. */}
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/*" element={<ConsoleRoutes />} />
    </Routes>
  );
}

function ConsoleRoutes() {
  const { status } = useAuth();

  if (status === 'checking') {
    // A brief, quiet hold rather than a flash of the login form for someone
    // who is already signed in.
    return (
      <div className="gate">
        <div className="row" style={{ gap: 'var(--s3)', color: 'var(--ink-faint)' }}>
          <HeronMark size={22} />
          <span className="small">Checking your session…</span>
        </div>
      </div>
    );
  }

  if (status === 'signed-out') {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route element={<AppShell />} path="/">
        <Route index element={<DashboardPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:id" element={<CompanyDetailPage />} />
        <Route path="licenses" element={<LicensesPage />} />
        {/* Writer pages: support is read-only on Overview/Companies/Licenses
            only, so these redirect them home rather than 403 every request. */}
        <Route path="products" element={<WriterOnly><ProductsPage /></WriterOnly>} />
        <Route path="sources" element={<WriterOnly><SourcesPage /></WriterOnly>} />
        <Route path="audit-logs" element={<WriterOnly><AuditLogPage /></WriterOnly>} />
        <Route path="email-outbox" element={<WriterOnly><EmailOutboxPage /></WriterOnly>} />
        <Route path="demo-data" element={<WriterOnly><DemoDataPage /></WriterOnly>} />
        <Route path="staff" element={<AdminOnly><StaffPage /></AdminOnly>} />
        <Route path="login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Pages the support (read-only) role may not see. */
function WriterOnly({ children }: { children: ReactNode }) {
  const can = useCapabilities();
  return can.canWrite ? <>{children}</> : <Navigate to="/" replace />;
}

/** Admin-only pages (staff management). */
function AdminOnly({ children }: { children: ReactNode }) {
  const can = useCapabilities();
  return can.isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

function NotFound() {
  return (
    <section className="panel">
      <EmptyState
        title="That page doesn’t exist"
        body="The link may be from an older version of the console."
        action={
          <Button variant="primary" onClick={() => window.location.assign('/')}>
            Back to overview
          </Button>
        }
      />
    </section>
  );
}
